import { useState, useRef, useEffect, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Check, ChevronDown } from 'lucide-react';

/**
 * Searchable dropdown that supports:
 * - Type to filter options
 * - Enter to select highlighted/best-match item + move to next field
 * - If typed text has no match: auto-create as new value and select it
 * - Tab to move to next field (keeps current value)
 * - data-form-idx for form keyboard navigation
 */
export function SearchableSelect({
  value,
  onValueChange,
  options = [],       // [{value: string, label: string}]
  placeholder = 'Select...',
  className,
  allowCreate = true, // auto-create new values on Enter when no match
  onAutoCreate,       // callback when a new value is auto-created (for backend sync)
  'data-testid': testId,
  'data-form-idx': formIdx,
  onMoveToNext,       // callback to move to next field
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = options.find(o => o.value === value)?.label || (value || '');

  // When popover opens, focus the search input
  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = useCallback((val) => {
    onValueChange(val);
    setOpen(false);
    // If this is a new value not in options, call onAutoCreate
    if (onAutoCreate && !options.find(o => o.value === val)) {
      onAutoCreate(val);
    }
    if (onMoveToNext) setTimeout(() => onMoveToNext(), 50);
  }, [onValueChange, onMoveToNext, onAutoCreate, options]);

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    }
  };

  // Handle Enter in search input — select best match or auto-create
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const trimmed = search.trim();
      if (!trimmed) return;

      // Find exact or partial match (case-insensitive)
      const exact = options.find(o => o.label.toLowerCase() === trimmed.toLowerCase());
      if (exact) {
        handleSelect(exact.value);
        return;
      }

      // Find best partial match
      const partial = options.find(o => o.label.toLowerCase().startsWith(trimmed.toLowerCase()));
      if (partial) {
        handleSelect(partial.value);
        return;
      }

      // No match — auto-create if allowed
      if (allowCreate) {
        handleSelect(trimmed);
        return;
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
          data-form-idx={formIdx}
          tabIndex={0}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[80]" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            ref={inputRef}
            placeholder={`Type to search...`}
            value={search}
            onValueChange={setSearch}
            onKeyDown={handleInputKeyDown}
          />
          <CommandList>
            <CommandEmpty>
              {allowCreate && search.trim() ? (
                <button className="w-full text-left px-2 py-1.5 text-sm text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded cursor-pointer"
                  onClick={() => handleSelect(search.trim())}>
                  Create "{search.trim()}"
                </button>
              ) : 'No results'}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")} />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
